import type { CSSProperties } from 'react';
import { useEffect, useRef } from 'react';

import BpkPagination from '@skyscanner-internal/backpack-web/bpk-component-pagination';

import FlightEntryList from '../FlightEntryList';

import type { FlightEntryData } from '@skyscanner-internal/landing-pages-shared-types/types-shared/ArrivalsDepartures';

import STYLES from './FlightEntryPagination.module.scss';

interface FlightEntryPaginationProps {
  entries: FlightEntryData[];
  error?: boolean;
  onRetryButtonClick?: () => void;
  currentPage?: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
  showPagination: boolean;
  loaded: boolean;
  isFiltering?: boolean;
  showFlightTrackerLinks?: boolean;
  flightTrackerBasePath?: string;
  useUMSFlightTrackerUrls?: boolean;
}

const FlightEntryPagination = ({
  currentPage = 0,
  entries,
  error = undefined,
  flightTrackerBasePath = undefined,
  isFiltering = false,
  loaded,
  onPageChange,
  onRetryButtonClick = undefined,
  pageSize = 20,
  showFlightTrackerLinks = false,
  showPagination,
  useUMSFlightTrackerUrls = false,
}: FlightEntryPaginationProps) => {
  const pageCount = Math.ceil(entries.length / pageSize);
  const pageSliceStart = currentPage * pageSize;
  const pageSliceEnd = pageSliceStart + pageSize;

  const listRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (showFlightTrackerLinks)
      listRef.current?.querySelector<HTMLElement>('a')?.focus();
  }, [currentPage, showFlightTrackerLinks]);

  return (
    <>
      <div ref={listRef}>
        <FlightEntryList
          entries={entries.slice(pageSliceStart, pageSliceEnd)}
          error={error}
          onRetryButtonClick={onRetryButtonClick}
          loaded={loaded}
          isFiltering={isFiltering}
          showFlightTrackerLinks={showFlightTrackerLinks}
          flightTrackerBasePath={flightTrackerBasePath}
          useUMSFlightTrackerUrls={useUMSFlightTrackerUrls}
        />
      </div>
      {showPagination && entries.length !== 0 && (
        <div
          className={STYLES.FlightEntryPagination}
          style={
            {
              '--bpk-button-primary-on-dark-background-color': 'transparent',
            } as CSSProperties
          }
        >
          <BpkPagination
            pageCount={pageCount}
            selectedPageIndex={currentPage}
            onPageChange={onPageChange}
            previousLabel="previous"
            nextLabel="next"
            visibleRange={3}
            pageLabel={(page: number) => `page ${page}`}
            paginationLabel="default"
          />
        </div>
      )}
    </>
  );
};

export default FlightEntryPagination;
